import { useState, useEffect, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import Server from "../../Shared/Server"
import BackButton from "../../Shared/BackButton"
import { InputField, NullableInput } from "../../Shared/InputField"
import SelectField from "../../Shared/SelectField"
import AsyncSelectField from "../../Shared/AsyncSelect"
import { location_categories_list } from "../../Shared/List"
import FileField from "../../Shared/FileField"

const INITIAL_FORM_DATA = {
  category: "",
  remark: "",
  contact_number: "",
  customer_id: "",
  contract: "",
  zone_id: "",
  village_id: "",
  line_id: "",
  station_id: "",
  rate_id: "",
  group_id: "",
  date: new Date().toISOString().slice(0, 10),
  description: "",

  serial_number: "",
  initial_reading: "",
  lock_number: "",
  meter_category_id: "",
}

const MultiPartForm = () => {
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)

  const [categoryOptions, setCategoryOptions] = useState([])
  const [meterCategoryOptions, setMeterCategoryOptions] = useState([])
  const [zoneOptions, setZoneOptions] = useState([])
  const [villageOptions, setVillageOptions] = useState([])
  const [lineOptions, setLineOptions] = useState([])
  const [stationOptions, setStationOptions] = useState([])
  const [rateOptions, setRateOptions] = useState([])
  const [customerOptions, setCustomerOptions] = useState([])
  const [groupOptions, setGroupOptions] = useState([])

  const [selectedVillage, setSelectedVillage] = useState(null)
  const [selectedStation, setSelectedStation] = useState(null)

  const [zonesLoading, setZonesLoading] = useState(false)
  const [linesLoading, setLinesLoading] = useState(false)

  const [formData, setFormData] = useState(INITIAL_FORM_DATA)

  const addAnotherRef = useRef(false)
  const today = new Date().toISOString().slice(0, 10)

  const {
    category, remark, contact_number, contract, customer_id, zone_id, village_id,
    line_id, station_id, rate_id, group_id, date, description,
    serial_number, initial_reading, lock_number, meter_category_id,
  } = formData

  const navigate = useNavigate()

  // Seed category options once
  useEffect(() => {
    setCategoryOptions(location_categories_list.map(cat => ({
      id: cat.id, key: cat.key, name: cat.name,
    })))
  }, [])

  // Load zones when village changes
  useEffect(() => {
    if (!selectedVillage?.value) {
      setZoneOptions([])
      return
    }
    setZonesLoading(true)
    loadOptions('village-zones', '', null, selectedVillage.value)
      .then((res) => {
        const zones = [{ id: null, key: "", name: "-- please select zone --" }]
        res.forEach(zone => {
          zones.push({ id: zone.value, key: zone.public_id, name: zone.label })
        })
        setZoneOptions(zones)
      })
      .finally(() => setZonesLoading(false))
  }, [selectedVillage])

  // Load lines when station changes
  useEffect(() => {
    if (!selectedStation?.value) {
      setLineOptions([])
      return
    }
    setLinesLoading(true)
    loadOptions('station-lines', '', null, null, selectedStation.value)
      .then((res) => {
        const lines = [{ id: null, key: "", name: "-- please select line --" }]
        res.forEach(line => {
          lines.push({ id: line.value, key: line.public_id, name: line.label })
        })
        setLineOptions(lines)
      })
      .finally(() => setLinesLoading(false))
  }, [selectedStation])

  const loadOptions = async (url, query, setOptions, villageId = null, stationId = null) => {
    try {
      const params = { q: query }
      if (url === 'village-zones' && villageId) params.village_id = villageId
      if (url === 'station-lines' && stationId) params.station_id = stationId

      const response = await Server.get(`/options/${url}`, { params })
      if (setOptions) setOptions(response.data)
      return response.data
    } catch (error) {
      return []
    }
  }

  const onChange = (e) => {
    const { name, files, value, type } = e.target
    const isFile = type === "file"
    const newValue = isFile ? (files?.[0] || "") : value

    setFormData((prev) => {
      const next = { ...prev, [name]: newValue }
      if (name === 'village_id') next.zone_id = ""
      if (name === 'station_id') next.line_id = ""
      return next
    })

    if (name === 'village_id') setSelectedVillage(e.target)
    if (name === 'station_id') setSelectedStation(e.target)
  }

  const validate = () => {
    const v = {}
    if (!formData.customer_id) v.customer_id = "Customer is required"
    if (!formData.contact_number) v.contact_number = "Contact is required"
    if (!formData.category) v.category = "Category is required"
    if (!formData.date) v.date = "Date is required"
    if (!formData.rate_id) v.rate_id = "Rate is required"
    if (!formData.village_id) v.village_id = "Village is required"
    if (!formData.zone_id) v.zone_id = "Zone is required"
    if (!formData.meter_category_id) v.meter_category_id = "Meter category is required"
    if (formData.initial_reading === "" || formData.initial_reading === null || formData.initial_reading === undefined) {
      v.initial_reading = "Initial reading is required"
    }
    return v
  }

  const focusFirstError = () => {
    setTimeout(() => {
      const el = document.querySelector('.is-invalid')
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        if (typeof el.focus === 'function') el.focus({ preventScroll: true })
      }
    }, 50)
  }

  const resetForm = () => {
    setFormData({ ...INITIAL_FORM_DATA, date: new Date().toISOString().slice(0, 10) })
    setErrors({})
    setSelectedVillage(null)
    setSelectedStation(null)
    setZoneOptions([])
    setLineOptions([])
  }

  const submit = (addAnother) => (e) => {
    e.preventDefault()
    addAnotherRef.current = !!addAnother

    const fieldErrors = validate()
    if (Object.keys(fieldErrors).length) {
      setErrors(fieldErrors)
      toast.error("Please fix the highlighted fields")
      focusFirstError()
      return
    }

    const selectedCategory = location_categories_list[Number(formData.category) - 1]
    if (!selectedCategory) {
      setErrors(prev => ({ ...prev, category: "Invalid category" }))
      toast.error("Please select a valid category")
      focusFirstError()
      return
    }

    const payload = {
      category: selectedCategory.name,
      remark, contact_number, customer_id, contract, zone_id, village_id,
      line_id, station_id, rate_id, group_id, date, description,
      serial_number, initial_reading, lock_number, meter_category_id,
    }

    saveData(payload)
  }

  const saveData = async (payload) => {
    setIsLoading(true)
    setErrors({})

    const dataToSend = new FormData()
    Object.entries(payload).forEach(([key, val]) => {
      if (key === 'contract') {
        if (val) dataToSend.append(key, val)
      } else {
        dataToSend.append(key, val ?? "")
      }
    })

    try {
      const response = await Server.post('/locations/', dataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      toast.success(response.data?.detail)
      if (addAnotherRef.current) {
        resetForm()
        addAnotherRef.current = false
      } else {
        navigate('/locations-serverside')
      }
    } catch (error) {
      const status = error?.response?.status
      if (status === 401) {
        navigate('/login')
      } else if (status === 422) {
        const fieldErrors = {}
        error.response?.data?.detail?.forEach(err => {
          fieldErrors[err.loc[1]] = err.msg
        })
        setErrors(fieldErrors)
        toast.error("Please fix the highlighted fields")
        focusFirstError()
      } else if (status === 403) {
        toast.error(error.response.data.detail)
        navigate('/')
      } else {
        toast.error(error?.response?.data?.detail || 'Failed to save location')
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <div className="row">
        <div className="col-md-12">
          <BackButton to="/locations-serverside" className="float-start" />
        </div>
      </div>
      <div className="card">
        <div className="card-header p-2 bg-info text-white d-flex justify-content-center">
          <h3 className="card-title mb-0">Location & Meter Information</h3>
        </div>
        <div className="card-body">
          <form onSubmit={submit(false)}>

            {/* LOCATION INFO */}
            <fieldset className="border p-3 mb-3" disabled={isLoading}>
              <legend className="w-auto px-2">Location Info</legend>
              <div className="row">
                <div className="col-md-4">
                  <AsyncSelectField
                    name="customer_id"
                    label="Customer"
                    required
                    autoFocus
                    value={customer_id}
                    onChange={onChange}
                    loadOptions={(q) => loadOptions('customers', q, setCustomerOptions)}
                    options={customerOptions}
                    placeholder="Start typing to search..."
                    error={errors.customer_id}
                  />
                </div>
                <div className="col-md-3">
                  <InputField
                    placeholder="Remark"
                    name="remark"
                    type="text"
                    label="Remark"
                    value={remark}
                    onChange={onChange}
                    error={errors.remark}
                  />
                </div>
                <div className="col-md-3">
                  <InputField
                    placeholder="e.g. 612345678"
                    name="contact_number"
                    type="text"
                    inputMode="tel"
                    label="Location Contact"
                    required
                    value={contact_number}
                    onChange={onChange}
                    error={errors.contact_number}
                  />
                </div>
                <div className="col-md-2">
                  <SelectField
                    name="category"
                    label="Category"
                    required
                    value={category === "" ? "" : parseInt(category)}
                    options={categoryOptions}
                    onChange={onChange}
                    error={errors.category}
                  />
                </div>
                <div className="col-md-6">
                  <NullableInput
                    placeholder="Location Detail"
                    name="description"
                    type="text"
                    label="Description (Optional)"
                    value={description}
                    onChange={onChange}
                    error={errors.description}
                  />
                </div>
                <div className="col-md-3">
                  <InputField
                    name="date"
                    type="date"
                    label="Date"
                    required
                    max={today}
                    value={date}
                    onChange={onChange}
                    error={errors.date}
                  />
                </div>
                <div className="col-md-3">
                  <FileField
                    name="contract"
                    type="file"
                    label="Contract (Optional)"
                    value={contract}
                    onChange={onChange}
                    error={errors.contract}
                  />
                </div>
              </div>
            </fieldset>

            {/* ADDRESS & RATE INFO */}
            <fieldset className="border p-3 mb-3" disabled={isLoading}>
              <legend className="w-auto px-2">Address & Rate Info</legend>
              <div className="row">
                <div className="col-md-3">
                  <AsyncSelectField
                    name="group_id"
                    label="Group"
                    value={group_id}
                    onChange={onChange}
                    loadOptions={(q) => loadOptions('location-groups', q, setGroupOptions)}
                    options={groupOptions}
                    error={errors.group_id}
                  />
                </div>
                <div className="col-md-3">
                  <AsyncSelectField
                    name="rate_id"
                    label="Rate"
                    required
                    value={rate_id}
                    onChange={onChange}
                    loadOptions={(q) => loadOptions('rates', q, setRateOptions)}
                    options={rateOptions}
                    error={errors.rate_id}
                  />
                </div>
                <div className="col-md-3">
                  <AsyncSelectField
                    name="village_id"
                    label="Village"
                    required
                    value={village_id}
                    onChange={onChange}
                    loadOptions={(q) => loadOptions('villages', q, setVillageOptions)}
                    options={villageOptions}
                    error={errors.village_id}
                  />
                </div>
                <div className="col-md-3">
                  <SelectField
                    name="zone_id"
                    label={zonesLoading ? "Zone (loading…)" : (!selectedVillage ? "Zone (select village first)" : "Zone")}
                    required
                    disabled={!selectedVillage || zonesLoading}
                    value={zone_id === "" ? "" : parseInt(zone_id)}
                    options={zoneOptions}
                    onChange={onChange}
                    error={errors.zone_id}
                  />
                </div>
                <div className="col-md-6">
                  <AsyncSelectField
                    name="station_id"
                    label="Station (Optional)"
                    value={station_id}
                    onChange={onChange}
                    loadOptions={(q) => loadOptions('stations', q, setStationOptions)}
                    options={stationOptions}
                    error={errors.station_id}
                  />
                </div>
                <div className="col-md-6">
                  <SelectField
                    name="line_id"
                    label={linesLoading ? "Line (loading…)" : (!selectedStation ? "Line (select station first)" : "Line (Optional)")}
                    disabled={!selectedStation || linesLoading}
                    value={line_id === "" ? "" : parseInt(line_id)}
                    options={lineOptions}
                    onChange={onChange}
                    error={errors.line_id}
                  />
                </div>
              </div>
            </fieldset>

            {/* METER INFO */}
            <fieldset className="border p-3 mb-3" disabled={isLoading}>
              <legend className="w-auto px-2">Meter Info</legend>
              <div className="row">
                <div className="col-md-6">
                  <AsyncSelectField
                    name="meter_category_id"
                    label="Category"
                    required
                    value={meter_category_id}
                    onChange={onChange}
                    loadOptions={(q) => loadOptions('meter-categories', q, setMeterCategoryOptions)}
                    options={meterCategoryOptions}
                    error={errors.meter_category_id}
                  />
                </div>
                <div className="col-md-6">
                  <InputField
                    placeholder="Initial Reading"
                    name="initial_reading"
                    type="number"
                    label="Initial Reading"
                    required
                    min="0"
                    step="0.01"
                    value={initial_reading}
                    onChange={onChange}
                    error={errors.initial_reading}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-md-6">
                  <InputField
                    placeholder="Serial Number"
                    name="serial_number"
                    type="text"
                    label="Serial Number (Optional)"
                    value={serial_number}
                    onChange={onChange}
                    error={errors.serial_number}
                  />
                </div>
                <div className="col-md-6">
                  <NullableInput
                    placeholder="Box Lock Number"
                    name="lock_number"
                    type="text"
                    label="Lock Number (Optional)"
                    value={lock_number}
                    onChange={onChange}
                    error={errors.lock_number}
                  />
                </div>
              </div>
            </fieldset>

            <div className="row">
              <div className="col-md-12 d-flex justify-content-end gap-2">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={resetForm}
                  disabled={isLoading}
                >
                  Reset
                </button>
                <button
                  type="button"
                  className="btn btn-outline-success"
                  onClick={submit(true)}
                  disabled={isLoading}
                >
                  {isLoading && addAnotherRef.current ? 'Saving…' : 'Save & Add Another'}
                </button>
                <button
                  type="submit"
                  className="btn btn-outline-info"
                  disabled={isLoading}
                >
                  {isLoading && !addAnotherRef.current ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  )
}

export default MultiPartForm
